import {
  TextInput,
  Textarea,
  Stack,
  Select,
  Switch,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useMemo } from "react";
import { SurgeryCenterReportDelivery } from "../../../services/admin/surgeryCenterReportDelivery.service";
import {
  scrollToFirstError,
  required,
  email,
  phone,
  fax,
} from "../../../utils/formValidation";

export interface SurgeryCenterReportDeliveryFormValues {
  surgeryCenterLabId: string;
  reportDeliveryMethodId: string;
  email: string;
  phone: string;
  fax: string;
  address: string;
  isActive: boolean;
  remoteConnectionIdentifier: string;
}

interface SurgeryCenterReportDeliveryFormProps {
  initialValues?: SurgeryCenterReportDelivery;
  onSubmit: (values: SurgeryCenterReportDeliveryFormValues) => void;
  surgeryCenters: Array<{ value: string; label: string }>;
  deliveryMethods: Array<{ value: string; label: string }>;
}

function getMethodName(
  methodId: string,
  methods: Array<{ value: string; label: string }>
): string {
  const method = methods.find((m) => m.value === methodId);
  return method?.label?.toLowerCase() || "";
}

export default function SurgeryCenterReportDeliveryForm({
  initialValues,
  onSubmit,
  surgeryCenters,
  deliveryMethods,
}: SurgeryCenterReportDeliveryFormProps) {
  const form = useForm<SurgeryCenterReportDeliveryFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      surgeryCenterLabId: initialValues?.surgeryCenterLabId || "",
      reportDeliveryMethodId: initialValues?.reportDeliveryMethodId || "",
      email: initialValues?.email || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      address: initialValues?.address || "",
      isActive: initialValues?.isActive ?? true,
      remoteConnectionIdentifier:
        initialValues?.remoteConnectionIdentifier || "",
    },
    validate: (values) => {
      const methodName = getMethodName(
        values.reportDeliveryMethodId,
        deliveryMethods
      );

      let emailError: string | null = null;
      if (methodName === "email" || methodName.startsWith("portal")) {
        emailError =
          required("Email is required")(values.email) || email()(values.email);
      } else {
        emailError = email()(values.email);
      }

      let faxError: string | null = null;
      if (methodName === "fax") {
        faxError =
          required("Fax number is required")(values.fax) || fax()(values.fax);
      } else {
        faxError = fax()(values.fax);
      }

      let addressError: string | null = null;
      if (methodName === "mail" || methodName === "ship physical copy") {
        addressError = required("Mailing address is required")(values.address);
      }

      let remoteConnectionIdentifierError: string | null = null;
      if (
        methodName.includes("remote print") &&
        !values.remoteConnectionIdentifier?.trim()
      ) {
        remoteConnectionIdentifierError =
          "Connection identifier is required for Remote Print delivery";
      }

      return {
        surgeryCenterLabId: required("Surgery center / lab is required")(
          values.surgeryCenterLabId
        ),
        reportDeliveryMethodId: required("Delivery method is required")(
          values.reportDeliveryMethodId
        ),
        email: emailError,
        fax: faxError,
        address: addressError,
        phone: phone()(values.phone),
        remoteConnectionIdentifier: remoteConnectionIdentifierError,
      };
    },
  });

  const handleSubmit = form.onSubmit(
    useCallback(
      (values: SurgeryCenterReportDeliveryFormValues) => onSubmit(values),
      [onSubmit]
    ),
    useCallback(() => scrollToFirstError(form), [form])
  );

  const selectedMethodName = useMemo(
    () => getMethodName(form.values.reportDeliveryMethodId, deliveryMethods),
    [form.values.reportDeliveryMethodId, deliveryMethods]
  );

  const showEmailField = useMemo(
    () =>
      selectedMethodName === "email" || selectedMethodName.startsWith("portal"),
    [selectedMethodName]
  );
  const showFaxField = useMemo(
    () => selectedMethodName === "fax",
    [selectedMethodName]
  );
  const showAddressField = useMemo(
    () =>
      selectedMethodName === "mail" ||
      selectedMethodName === "ship physical copy",
    [selectedMethodName]
  );
  const showRemotePrintField = useMemo(
    () => selectedMethodName.includes("remote print"),
    [selectedMethodName]
  );
  const showPhoneField = useMemo(
    () =>
      selectedMethodName.startsWith("ship") || selectedMethodName === "mail",
    [selectedMethodName]
  );
  const showNoConfigMessage = useMemo(
    () => !!selectedMethodName && selectedMethodName.startsWith("hl7"),
    [selectedMethodName]
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Surgery Center / Lab"
          placeholder="Select surgery center / lab"
          withAsterisk
          searchable
          data={surgeryCenters}
          name="surgeryCenterLabId"
          {...form.getInputProps("surgeryCenterLabId")}
        />

        <Select
          label="Delivery Method"
          placeholder="Select delivery method"
          withAsterisk
          searchable
          data={deliveryMethods}
          name="reportDeliveryMethodId"
          {...form.getInputProps("reportDeliveryMethodId")}
        />

        {showEmailField && (
          <TextInput
            label="Email Address"
            placeholder="Enter email address"
            type="email"
            withAsterisk
            name="email"
            {...form.getInputProps("email")}
          />
        )}

        {showFaxField && (
          <TextInput
            label="Fax Number"
            placeholder="Enter fax number"
            withAsterisk
            name="fax"
            {...form.getInputProps("fax")}
          />
        )}

        {showAddressField && (
          <Textarea
            label="Mailing Address"
            placeholder="Enter mailing address"
            minRows={2}
            withAsterisk
            name="address"
            {...form.getInputProps("address")}
          />
        )}

        {showRemotePrintField && (
          <TextInput
            label="Connection Identifier"
            placeholder="e.g. clinic-printer-001"
            description="Must match the identifier configured in the Remote Print client app at the clinic"
            withAsterisk
            name="remoteConnectionIdentifier"
            {...form.getInputProps("remoteConnectionIdentifier")}
          />
        )}

        {showPhoneField && (
          <TextInput
            label="Phone (optional)"
            placeholder="Enter phone number"
            name="phone"
            {...form.getInputProps("phone")}
          />
        )}

        {showNoConfigMessage && (
          <Text size="sm" c="dimmed">
            No additional configuration required for this delivery method.
          </Text>
        )}

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
